/**
 * Send → Zoho: one Zoho Quote per package + its PDF, through n8n "[C] App 6 - Quotes" (REACT_APP_QUOTES_URL).
 *
 *   1. save   — Zoho creates the Quote (or updates the same one on a resend: lines replaced, 90 days restarted)
 *               and hands back its number (Q-123456)
 *   2. PDF    — built on the phone from quoteModel() with that number, uploaded to storage (bucket quote-pdfs)
 *   3. attach — the PDF goes on the Zoho Quote; an earlier PDF is removed, never duplicated
 *
 * The phone remembers each package's Zoho Quote id so a resend updates that exact quote.
 * No signal → queued (latest send wins) and done when the phone is back online.
 *   4. email  — once the PDFs are uploaded, the customer's report email is queued with them attached
 *               (services/reportEmail.js)
 */
import { getAccessToken, getSupabase } from './auth';
import { LEADS_SOURCE } from './leads';
import { queueChange, registerZohoAction } from './sync';
import { quoteModel, zohoQuotePayload } from './quoteDoc';
import { getSentEntry, saveSentEdit } from './drafts';
import { stampPackages } from './quoteSync';
import { emailReport } from './reportEmail';

export const QUOTES_URL = process.env.REACT_APP_QUOTES_URL;
export const QUOTES_LIVE = LEADS_SOURCE === 'zoho' && !!QUOTES_URL;
export const QUOTE_PDF_BUCKET = 'quote-pdfs';
const IDS_KEY = 'cie_zoho_quotes_v1';

/* ---------- remembered Zoho Quote ids: "<inspection>-<package>" → id ---------- */
const readIds = () => {
  try {
    return JSON.parse(localStorage.getItem(IDS_KEY) || '{}');
  } catch {
    return {};
  }
};
const rememberIds = (inspectionId, quotes) => {
  const ids = readIds();
  quotes.forEach((q) => {
    ids[`${inspectionId}-${q.key}`] = q.zohoId;
  });
  try {
    localStorage.setItem(IDS_KEY, JSON.stringify(ids));
  } catch {
    /* storage full — the workflow can still find the quote by its key */
  }
};
export const rememberedQuoteId = (inspectionId, pkgId) => readIds()[`${inspectionId}-${pkgId}`] || null;

/* ---------- calls ---------- */
const noSignal = (message) => Object.assign(new Error(message), { offline: true });

export const callQuotes = async (body) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to save quotes to Zoho.');
  let res;
  try {
    res = await fetch(QUOTES_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    throw noSignal('No connection — quotes will go to Zoho when you’re back online.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `Zoho didn’t save the quotes (${res.status}).`);
    err.offline = res.status >= 500;
    throw err;
  }
  return out;
};

const uploadPdf = async (blob) => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('PDF storage not connected');
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${new Date().toISOString().slice(0, 7)}/${id}.pdf`;
  const { error } = await supabase.storage.from(QUOTE_PDF_BUCKET).upload(path, blob, { contentType: 'application/pdf', upsert: false });
  if (error) throw noSignal(`Couldn’t upload a quote PDF — ${error.message || 'will retry'}`);
  return supabase.storage.from(QUOTE_PDF_BUCKET).getPublicUrl(path).data.publicUrl;
};

/* The sent inspection remembers which Zoho quote (and which version of it) each package became —
   that's how a later open spots edits made in Zoho (two-way sync, services/quoteSync.js). */
const stampSent = (draftId, saved) => {
  const entry = draftId ? getSentEntry(draftId) : null;
  if (!entry || !entry.source || !entry.source.quoteState) return;
  const qs = entry.source.quoteState;
  saveSentEdit({ ...entry.source, id: draftId, quoteState: { ...qs, packages: stampPackages(qs.packages || [], saved) } });
};

/* What the queue runs when this send goes out. */
export const pushQuotes = async (job) => {
  const { inspectionId, packages, formats, customer, agent } = job;
  const saved = await callQuotes({
    action: 'save',
    inspectionId,
    packages: packages.map((p) => zohoQuotePayload(p, formats[p.id], rememberedQuoteId(inspectionId, p.id))),
  });
  rememberIds(inspectionId, saved.quotes);
  stampSent(job.draftId, saved.quotes);

  // the PDFs carry Zoho's quote number and today's date (valid 90 days from now)
  const { quotePdfBlob } = await import('../pages/Routes/QuotePdf');
  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/cie-logo-navy-tight.png` : '';
  const sentAt = new Date();
  const attach = [];
  for (const q of saved.quotes) {
    const pkg = packages.find((p) => String(p.id) === String(q.key));
    if (!pkg) continue;
    const model = quoteModel({ pkg, format: formats[pkg.id], customer, agent, quoteNumber: q.quoteNumber, sentAt });
    const blob = await quotePdfBlob(model, logoUrl);
    const pdfUrl = await uploadPdf(blob);
    attach.push({ zohoId: q.zohoId, pdfUrl, fileName: `Quote ${q.quoteNumber} - ${pkg.name || 'Quote'}` });
  }
  if (attach.length) await callQuotes({ action: 'attach', quotes: attach });
  // the PDFs exist now — the customer's report email goes out with them attached
  if (job.email) emailReport({ inspectionId, email: job.email, pdfs: attach.map((a) => ({ url: a.pdfUrl, fileName: a.fileName })) });
  return { quotes: saved.quotes };
};

registerZohoAction('send_quotes', (zoho) => pushQuotes(zoho));

/**
 * Review & send → Send. Never holds the agent up: it runs through the sync queue (straight away when
 * there's signal), and a newer send for the same inspection replaces one still waiting.
 * Quote-only sends (no Zoho appointment) and test mode skip Zoho.
 */
export const sendQuotes = ({ inspectionId, draftId = null, packages, formats = {}, customer = {}, agent = '', email = null }) => {
  if (!QUOTES_LIVE || !inspectionId || !packages.length) return { skipped: true };
  const job = {
    action: 'send_quotes',
    inspectionId,
    draftId,
    // only what the quotes need — lines carry their Zoho product ids, quantities, prices and descriptions
    packages: packages.map(({ id, name, services, discount, zohoId }) => ({ id, name, services, discount, zohoId })),
    formats,
    customer,
    agent,
    email, // the report email that follows the PDFs (null → none)
  };
  queueChange({
    type: 'quotes.send',
    leadId: inspectionId,
    label: `${customer.name || 'Customer'} — ${packages.length} quote${packages.length === 1 ? '' : 's'} saved to Zoho`,
    payload: { quotes: packages.length },
    zoho: job,
    supersede: `quotes:${inspectionId}`,
  });
  return { queued: true };
};
